import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, LoaderCircle, Pause, Play, RotateCcw } from 'lucide-react';

import { OutletLogo } from '@/components/feed/FeedCard';
import { HighlightedSelections } from '@/components/HighlightedSelections';
import {
  SynchronizedYoutubePlayer,
  type SynchronizedYoutubePlayerHandle,
} from '@/components/SynchronizedYoutubePlayer';
import { Button } from '@/components/ui/button';
import { SeekIcon } from '@/components/videos/VideoPlayerWithControls';
import { useYoutubeCaptions } from '@/hooks/useYoutubeCaptions';
import { FeedItemDto } from '@/generated/api-client';
import type { CardChatExampleSet, FeedItem } from '@/services';
import { timeAgo } from '@/utils/timeAgo';

const toFeedItem = (
  item: CardChatExampleSet['news'][number],
  meaningId: string,
  phraseText: string
) =>
  new FeedItemDto({
    matchId: `news:${item.sentenceId}`,
    meaningId,
    phraseText,
    sentence: item.sentence,
    matchedTokens: item.highlightSpans,
    articleUrl: item.articleUrl,
    articleTitle: item.articleTitle,
    articleDomain: item.articleDomain,
    siteName: item.siteName,
    logoUrl: item.logoUrl,
    publishedAtUtc: item.publishedAtUtc,
    createdAtUtc: item.createdAtUtc,
    segmentId: item.sentenceId,
  });

const pagerButtonClassName =
  'flex size-11 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition-colors hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-[#496653] focus-visible:outline-none disabled:text-slate-300 disabled:hover:bg-white';

const controlButtonClassName =
  'size-10 rounded-full border-slate-200 bg-white text-slate-700 hover:bg-slate-50';

function ExamplePager({
  noun,
  position,
  count,
  onChange,
}: {
  noun: string;
  position: number;
  count: number;
  onChange: (position: number) => void;
}) {
  if (count < 2) return null;

  return (
    <div className="flex items-center justify-between gap-3">
      <button
        type="button"
        aria-label={`Previous ${noun.toLowerCase()}`}
        disabled={position === 0}
        onClick={() => onChange(position - 1)}
        className={pagerButtonClassName}
      >
        <ChevronLeft className="size-5" aria-hidden="true" />
      </button>
      <div className="flex flex-col items-center gap-2">
        <span className="text-sm font-semibold text-slate-900" aria-live="polite">
          {noun} {position + 1} of {count}
        </span>
        <div className="flex items-center gap-1.5" aria-hidden="true">
          {Array.from({ length: count }, (_, dotIndex) => (
            <span
              key={dotIndex}
              className={`h-1.5 rounded-full transition-all ${dotIndex === position ? 'w-5 bg-slate-900' : 'w-1.5 bg-slate-300'}`}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        aria-label={`Next ${noun.toLowerCase()}`}
        disabled={position === count - 1}
        onClick={() => onChange(position + 1)}
        className={pagerButtonClassName}
      >
        <ChevronRight className="size-5" aria-hidden="true" />
      </button>
    </div>
  );
}

function NewsExampleCard({ item }: { item: FeedItem }) {
  const outletName = item.siteName ?? item.articleDomain;
  const highlights = (item.matchedTokens ?? [])
    .map((span) => ({ start: span.start, length: span.end - span.start }))
    .sort((left, right) => left.start - right.start);

  return (
    <article className="space-y-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div className="flex items-center gap-3">
        <OutletLogo item={item} compact />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">{outletName}</p>
          {item.publishedAtUtc && <p className="text-xs text-slate-500">{timeAgo(item.publishedAtUtc)}</p>}
        </div>
      </div>
      <blockquote className="text-[15px] leading-relaxed text-slate-800 italic">
        “<HighlightedSelections text={item.sentence} selections={highlights} />”
      </blockquote>
      <a
        href={item.articleUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex max-w-full items-center gap-1.5 text-sm font-semibold text-[#496653] underline hover:text-[#2f4938]"
      >
        <span className="truncate">Open article</span>
        <ExternalLink className="size-3.5 shrink-0" aria-hidden="true" />
      </a>
    </article>
  );
}

function NewsExampleCarousel({
  meaningId,
  examples,
}: {
  meaningId: string;
  examples: CardChatExampleSet;
}) {
  const [index, setIndex] = useState(0);
  const items = examples.news ?? [];
  if (items.length === 0) return null;

  const position = Math.min(index, items.length - 1);

  return (
    <section aria-label={`News examples for ${examples.query}`} className="mr-auto w-full max-w-[92%] space-y-3">
      <p className="text-[10.5px] font-medium tracking-wider text-[#97917f] uppercase">In the news</p>
      <NewsExampleCard
        key={items[position].sentenceId}
        item={toFeedItem(items[position], meaningId, examples.query)}
      />
      <ExamplePager noun="Source" position={position} count={items.length} onChange={setIndex} />
    </section>
  );
}

function VideoExampleCarousel({ examples }: { examples: CardChatExampleSet }) {
  const [index, setIndex] = useState(0);
  const [playerStatus, setPlayerStatus] = useState({ loading: true, error: false });
  const [playing, setPlaying] = useState(false);
  const playerHandleRef = useRef<SynchronizedYoutubePlayerHandle>(null);
  const items = examples.videos ?? [];
  const position = Math.min(index, Math.max(items.length - 1, 0));
  const video = items[position];
  const { captions, loading, error } = useYoutubeCaptions(video?.videoId);
  if (!video) return null;

  const playbackDisabled = playerStatus.loading || playerStatus.error;

  const move = (next: number) => {
    setPlayerStatus({ loading: true, error: false });
    setPlaying(false);
    setIndex(next);
  };

  return (
    <section aria-label={`Video examples for ${examples.query}`} className="mr-auto w-full max-w-[92%] space-y-3">
      <p className="text-[10.5px] font-medium tracking-wider text-[#97917f] uppercase">In videos</p>
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <SynchronizedYoutubePlayer
          key={video.sentenceId}
          ref={playerHandleRef}
          videoId={video.videoId}
          playbackStartMilliseconds={video.playbackStartMilliseconds}
          sentenceId={video.sentenceId}
          highlightSpans={video.highlightSpans}
          captions={captions}
          captionsLoading={loading}
          captionsError={error}
          onStatusChange={setPlayerStatus}
          onPlayingChange={setPlaying}
        />
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Replay"
            disabled={playbackDisabled}
            onClick={() => playerHandleRef.current?.replay()}
            className={controlButtonClassName}
          >
            <RotateCcw className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Back 5 seconds"
            disabled={playbackDisabled}
            onClick={() => playerHandleRef.current?.seekBy(-5)}
            className={controlButtonClassName}
          >
            <SeekIcon direction="back" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={playing ? 'Pause' : 'Play'}
            disabled={playbackDisabled}
            onClick={() => playerHandleRef.current?.togglePlay()}
            className={`${controlButtonClassName} text-slate-800`}
          >
            {playing ? <Pause className="size-4 fill-current" /> : <Play className="size-4 fill-current" />}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Forward 5 seconds"
            disabled={playbackDisabled}
            onClick={() => playerHandleRef.current?.seekBy(5)}
            className={controlButtonClassName}
          >
            <SeekIcon direction="forward" />
          </Button>
        </div>
      </div>
      <ExamplePager noun="Video" position={position} count={items.length} onChange={move} />
    </section>
  );
}

interface ChatExamplesProps {
  meaningId: string;
  examples: CardChatExampleSet;
}

export function ChatExamples({ meaningId, examples }: ChatExamplesProps) {
  return (
    <>
      <NewsExampleCarousel meaningId={meaningId} examples={examples} />
      <VideoExampleCarousel examples={examples} />
    </>
  );
}

export function SearchingExamples() {
  return (
    <div className="mr-auto flex w-fit items-center gap-2 py-1 text-sm text-slate-500">
      <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
      Searching examples…
    </div>
  );
}
